<template>
  <now-playing-section-layout :compact-on-mobile="compactOnMobile">
    <template #now-playing-image>
      <now-playing-image
        :image-src="nowPlayingImageSrc"
        :width="rectangleImageWidth"
        :height="rectangleImageHeight"
        :compact-on-mobile="compactOnMobile"
        @image-loaded="onImageLoaded"
      >
      </now-playing-image>
    </template>
    <template #now-playing-detail>
      <now-playing-detail
        :station="station"
        :dark-theme="darkTheme"
        :show-title="showTitle"
        :episode-name="episodeName"
        :episode-audio-file-src="episodeAudioFileSrc"
        :playing-live-stream="canPlayLiveStream"
        @audio-player:ready="onAudioPlayerReady"
        @audio:pause="onNowPlayingPause"
        @audio:play="onNowPlayingPlay"
        @episode-ended="onEpisodeEnded"
      >
      </now-playing-detail>
    </template>
  </now-playing-section-layout>
</template>

<script>
import NowPlayingSectionLayout from './NowPlayingSectionLayout';
import NowPlayingImage from './NowPlayingImage';
import NowPlayingDetail from './NowPlayingDetail';
import { getLiveStreamUrl, urlGetter } from '../utils/utils';
import { placeholderImage } from '../utils/placeholder';

export default {
  name: 'now-playing-section',
  // Vue3 (LP-4/MR-78, the LP-2-deferred emits item): declare the events this
  // component $emits so parent `@audio-player:ready`/`@audio:play`/`@audio:pause`
  // bind as component events, not undeclared native-attr fallthrough.
  emits: ['audio-player:ready', 'audio:pause', 'audio:play', 'image-loaded'],
  components: {
    NowPlayingSectionLayout,
    NowPlayingImage,
    NowPlayingDetail,
  },
  props: {
    station: String,
    showTitle: String,
    darkTheme: Boolean,
    episodeImages: Boolean,
    showImageSrc: String,
    activeEpisode: Object,
    compactOnMobile: Boolean,
    dropToLive: Boolean,
    useM4a: Boolean,
    apiPrefix: String,
  },
  data() {
    return {
      rectangleImageWidth: 700,
      rectangleImageHeight: 366,
      squareImageWidth: undefined,
      squareImageHeight: undefined,
      liveStreamUrl: undefined,
      canPlayLiveStream: false,
    };
  },
  computed: {
    rootUrl() {
      return `${this.apiPrefix}/${this.station.toLowerCase()}`;
    },
    showImageUrl() {
      return this.showImageSrc
        ? urlGetter(this.apiPrefix, this.showImageSrc)
        : placeholderImage(
          this.rectangleImageWidth,
          this.rectangleImageHeight,
          this.showTitle || (this.station ? this.station.toUpperCase() : ''));
    },
    nowPlayingImageSrc() {
      if (this.episodeImages) {
        return this.activeEpisode
          ? this.activeEpisode.image &&
              urlGetter(this.apiPrefix, this.activeEpisode.image)
          : this.showImageUrl;
      }
      return this.showImageUrl;
    },
    episodeName() {
      return this.activeEpisode ? this.activeEpisode.name : undefined;
    },
    m3u8Url() {
      return this.activeEpisode
        ? `${this.rootUrl}/playlist/${this.activeEpisode.id}.m3u8`
        : undefined;
    },
    m4aUrl() {
      return this.activeEpisode
        ? `${this.apiPrefix}${this.activeEpisode.m4a_file}`
        : undefined;
    },
    episodeAudioFileSrc() {
      if (!this.canPlayLiveStream) {
        if (this.useM4a) {
          return this.m4aUrl;
        }
        return this.m3u8Url;
      }
      return this.liveStreamUrl;
    },
  },
  methods: {
    // Relay the cover-loaded resize signal up to V2Player -> app.vue. Slot
    // content does NOT bubble on its own, so every hop has to forward it.
    onImageLoaded() {
      this.$emit('image-loaded');
    },

    onAudioPlayerReady(audioPlayerRef) {
      this.$emit('audio-player:ready', audioPlayerRef);
    },
    onNowPlayingPause() {
      this.$emit('audio:pause');
    },
    onNowPlayingPlay() {
      this.$emit('audio:play');
    },
    onEpisodeEnded() {
      if (this.dropToLive) {
        getLiveStreamUrl(this.station)
          .then((url) => {
            this.liveStreamUrl = url;
            this.canPlayLiveStream = true;
          })
          .catch((e) => {
            // eslint-disable-next-line no-console
            console.warn('Unable to fetch live stream', e);
          });
      }
    },
  },
  watch: {
    activeEpisode() {
      this.canPlayLiveStream = false;
    },
  },
};
</script>

<style lang="scss" scoped></style>
